/**
 * Superadmin API hooks (KraetdTech ops only).
 *
 * Endpoints under /api/v1/superadmin/. Backend implementation pending — these
 * hooks call real endpoints; React Query surfaces empty state cleanly until
 * the backend lands. When the backend is ready, no frontend changes needed.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

// ─── Types ──────────────────────────────────────────────────────────────

export interface SuperadminTenant {
  id: string;
  slug: string;
  display_name: string;
  employees_count: number;
  plan: "TRIAL" | "MONTHLY" | "ANNUAL";
  status: "ACTIVE" | "SUSPENDED" | "TRIAL";
  created_at: string;
}

export interface SupportSession {
  id: string;
  tenant_id: string;
  tenant_name: string;
  reason: string;
  started_at: string;
  ended_at: string | null;
  actor_email: string;
}

export interface SuperadminAuditLog {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  actor_email: string;
  action: string;
  target_table: string;
  target_id: string;
  ip_address: string;
  reason: string | null;
  is_superadmin: boolean;
  created_at: string;
}

// ─── Tenants ────────────────────────────────────────────────────────────

export function useSuperadminTenants(params: {
  search?: string;
  status?: string;
  plan?: string;
}) {
  return useQuery({
    queryKey: ["superadmin", "tenants", params],
    queryFn: async () => {
      const r = await api.get<{ results: SuperadminTenant[]; count: number }>(
        "/v1/superadmin/tenants/",
        { params }
      );
      return r.data;
    },
  });
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const r = await api.post(`/v1/superadmin/tenants/${id}/suspend/`, { reason });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] }),
  });
}

// ─── Support sessions ───────────────────────────────────────────────────

export function useStartSupportSession() {
  return useMutation({
    mutationFn: async ({ tenantId, reason }: { tenantId: string; reason: string }) => {
      const r = await api.post<SupportSession>(
        `/v1/superadmin/tenants/${tenantId}/support-session/`,
        { reason }
      );
      return r.data;
    },
  });
}

export function useEndSupportSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.post(`/v1/superadmin/support-sessions/${sessionId}/end/`);
    },
  });
}

export function useActiveSupportSession() {
  // Caller is responsible for only mounting this hook for superadmin users
  // (see SupportSessionBanner). retry:false keeps a missing endpoint from
  // storming the network in dev.
  return useQuery({
    queryKey: ["superadmin", "active-session"],
    queryFn: async () => {
      const r = await api.get<SupportSession | null>(
        "/v1/superadmin/support-sessions/active/"
      );
      return r.data;
    },
    refetchInterval: 60_000,
    retry: false,
  });
}

// ─── Audit log ──────────────────────────────────────────────────────────

export function useSuperadminAuditLogs(params: {
  tenant?: string;
  actor?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: ["superadmin", "audit", params],
    queryFn: async () => {
      const r = await api.get<{ results: SuperadminAuditLog[]; count: number }>(
        "/v1/superadmin/audit/",
        { params }
      );
      return r.data;
    },
  });
}
